/**
 * Vonalkód beolvasó oldal — dokumentum azonosítás + szerep-alapú akciók
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { get, post, downloadFile } from '../../lib/api';
import { PageHeader, Button, Badge, Card, Input, LoadingOverlay } from '../../components/ui';
import { useToast } from '../../components/ui/Toast';
import BarcodeScanner from '../../components/BarcodeScanner';

interface LookupResult {
  found: boolean;
  accessDenied?: boolean;
  message?: string;
  docType?: string;
  docTypeLabel?: string;
  docId?: string;
  docNumber?: string;
  barcodeId?: string;
  status?: string;
  issueDate?: string;
  grossAmount?: number | null;
  partnerName?: string;
  issuerName?: string;
  role?: 'issuer' | 'buyer' | 'unknown';
  category?: string;
  code?: string;
}

const STATUS_LABELS: Record<string, { label: string; variant: 'default' | 'info' | 'success' | 'error' | 'warning' }> = {
  draft: { label: 'Vázlat', variant: 'default' },
  issued: { label: 'Kiállítva', variant: 'info' },
  paid: { label: 'Fizetve', variant: 'success' },
  cancelled: { label: 'Törölve', variant: 'error' },
  storno: { label: 'Sztornózva', variant: 'warning' },
};

function fmtDate(iso: string | undefined) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('hu-HU', { year: 'numeric', month: 'short', day: 'numeric' }); } catch { return '—'; }
}
function fmtPrice(n: number | null | undefined) {
  if (n == null) return '—';
  return Math.round(n / 100).toLocaleString('hu-HU') + ' Ft';
}

export default function ScannerPage() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [scannerOpen, setScannerOpen] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<LookupResult | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  async function lookup(code: string) {
    const cleaned = code.trim().toUpperCase();
    if (!cleaned) return;
    setLoading(true);
    setResult(null);
    try {
      const data = await get<LookupResult>(`/barcode/lookup/${encodeURIComponent(cleaned)}`);
      setResult(data);
      if (!data.found) {
        toast('Dokumentum nem található ezzel a kóddal.', 'warning');
      }
    } catch (e: any) {
      toast(e.message || 'Keresés sikertelen.', 'error');
    }
    setLoading(false);
  }

  function handleScan(code: string) {
    setScannerOpen(false);
    setManualCode(code);
    lookup(code);
  }

  const r = result;
  const st = r?.status ? (STATUS_LABELS[r.status] || STATUS_LABELS.draft) : null;
  const isIssuer = r?.role === 'issuer';
  const isBuyer = r?.role === 'buyer';

  return (
    <div>
      <LoadingOverlay visible={loading} text="Dokumentum keresése..." />
      <LoadingOverlay visible={pdfLoading} text="PDF előkészítése..." />

      <PageHeader title="Vonalkód beolvasó" description="Számla, díjbekérő vagy szállítólevél azonosítása vonalkóddal." />

      {/* Input terület */}
      <Card style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <label className="form-label">Vonalkód (MOD-XXXXXXXX)</label>
            <Input
              value={manualCode}
              onChange={e => setManualCode(e.target.value.toUpperCase())}
              placeholder="MOD-A1B2C3D4"
              onKeyDown={e => { if (e.key === 'Enter') lookup(manualCode); }}
              style={{ fontFamily: 'monospace', letterSpacing: '1px', textTransform: 'uppercase' }}
            />
          </div>
          <Button onClick={() => lookup(manualCode)} disabled={!manualCode.trim()}>Keresés</Button>
          <Button variant="ghost" onClick={() => setScannerOpen(true)}>Kamera</Button>
        </div>
      </Card>

      {/* Eredmény */}
      {r && !r.found && (
        <Card style={{ textAlign: 'center', padding: '30px' }}>
          <div style={{ fontSize: '36px', marginBottom: '8px' }}>🔍</div>
          <div style={{ fontSize: '16px', fontWeight: 600, color: 'var(--slate)' }}>Nem található</div>
          <div style={{ fontSize: '13px', color: 'var(--slate)', marginTop: '4px' }}>
            A <code style={{ background: 'var(--mist)', padding: '2px 6px', borderRadius: '4px' }}>{r.code}</code> kóddal nem tartozik dokumentum a rendszerben.
          </div>
        </Card>
      )}

      {r && r.found && r.accessDenied && (
        <Card style={{ textAlign: 'center', padding: '30px' }}>
          <div style={{ fontSize: '36px', marginBottom: '8px' }}>🔒</div>
          <div style={{ fontSize: '16px', fontWeight: 600, color: 'var(--red)' }}>Hozzáférés megtagadva</div>
          <div style={{ fontSize: '13px', color: 'var(--slate)', marginTop: '4px' }}>
            {r.message || 'Ez a dokumentum nem tartozik a cégedhez sem kiállítóként, sem vevőként.'}
          </div>
        </Card>
      )}

      {r && r.found && !r.accessDenied && (
        <Card>
          {/* Szerep jelzés */}
          <div style={{
            padding: '10px 16px', borderRadius: 'var(--r)', marginBottom: '14px', textAlign: 'center', fontWeight: 700, fontSize: '13px',
            background: isIssuer ? '#eff6ff' : isBuyer ? '#fef3c7' : '#f3f4f6',
            color: isIssuer ? '#1e40af' : isBuyer ? '#92400e' : '#6b7280',
            border: `1px solid ${isIssuer ? '#bfdbfe' : isBuyer ? '#fde68a' : '#e5e7eb'}`,
          }}>
            {isIssuer ? '📤 Te vagy a kiállító — saját dokumentum' :
             '📥 Te vagy a vevő — bejövő dokumentum'}
          </div>

          {/* Dokumentum infó */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '14px', marginBottom: '16px' }}>
            <div>
              <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--slate)', fontWeight: 700, marginBottom: '3px' }}>Típus</div>
              <div style={{ fontWeight: 600, fontSize: '14px', color: 'var(--navy)' }}>{r.docTypeLabel}</div>
            </div>
            <div>
              <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--slate)', fontWeight: 700, marginBottom: '3px' }}>Sorszám</div>
              <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>{r.docNumber}</div>
            </div>
            <div>
              <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--slate)', fontWeight: 700, marginBottom: '3px' }}>Státusz</div>
              {st && <Badge variant={st.variant}>{st.label}</Badge>}
            </div>
            <div>
              <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--slate)', fontWeight: 700, marginBottom: '3px' }}>Dátum</div>
              <div style={{ fontSize: '14px', color: 'var(--ink)' }}>{fmtDate(r.issueDate)}</div>
            </div>
            {r.grossAmount != null && (
              <div>
                <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--slate)', fontWeight: 700, marginBottom: '3px' }}>Bruttó</div>
                <div style={{ fontWeight: 700, fontSize: '16px', color: 'var(--navy)' }}>{fmtPrice(r.grossAmount)}</div>
              </div>
            )}
          </div>

          {/* Felek */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
            <div style={{ padding: '10px 12px', background: 'var(--mist)', borderRadius: 'var(--r)' }}>
              <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', color: isIssuer ? '#2563eb' : 'var(--slate)', fontWeight: 700, marginBottom: '3px' }}>
                {isIssuer ? '📤 Kiállító (Te)' : 'Kiállító'}
              </div>
              <div style={{ fontWeight: 600, fontSize: '13px' }}>{r.issuerName || '—'}</div>
            </div>
            <div style={{ padding: '10px 12px', background: 'var(--mist)', borderRadius: 'var(--r)' }}>
              <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', color: isBuyer ? '#d97706' : 'var(--slate)', fontWeight: 700, marginBottom: '3px' }}>
                {isBuyer ? '📥 Vevő (Te)' : 'Vevő / Partner'}
              </div>
              <div style={{ fontWeight: 600, fontSize: '13px' }}>{r.partnerName || '—'}</div>
            </div>
          </div>

          {/* Vonalkód */}
          <div style={{ textAlign: 'center', padding: '8px', background: '#f9fafb', borderRadius: 'var(--r)', marginBottom: '16px' }}>
            <code style={{ fontSize: '14px', fontWeight: 700, letterSpacing: '2px', color: 'var(--navy)' }}>{r.barcodeId}</code>
          </div>

          {/* Akciók */}
          <div style={{ borderTop: '1px solid var(--line)', paddingTop: '14px' }}>
            {isIssuer && (
              <div>
                <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', color: '#2563eb', fontWeight: 700, marginBottom: '8px' }}>
                  Kiállítói műveletek
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {r.category === 'invoice' && (
                    <>
                      <Button onClick={() => navigate(`/szamlak?highlight=${r.docId}`)}>Megnyitás a számlákban</Button>
                      <Button variant="ghost" onClick={async () => {
                        setPdfLoading(true);
                        try { await downloadFile(`/invoices/${r.docId}/pdf`, `${r.docNumber}.pdf`); } catch { toast('PDF hiba.', 'error'); }
                        finally { setPdfLoading(false); }
                      }}>PDF letöltés</Button>
                      <Button variant="ghost" onClick={async () => {
                        try {
                          const preview = await get<any>(`/invoices/${r.docId}/email-preview`);
                          if (preview?.toEmail) {
                            await post(`/invoices/${r.docId}/send-email`, { toEmail: preview.toEmail });
                            toast(`Elküldve: ${preview.toEmail}`, 'success');
                          } else {
                            toast('Nincs beállítva e-mail cím.', 'warning');
                          }
                        } catch (e: any) { toast(e.message || 'Küldés sikertelen.', 'error'); }
                      }}>E-mail küldés</Button>
                    </>
                  )}
                  {r.category === 'delivery-note' && (
                    <>
                      <Button onClick={() => navigate(`/szallitolevelek?highlight=${r.docId}`)}>Megnyitás</Button>
                      <Button variant="ghost" onClick={async () => {
                        setPdfLoading(true);
                        try { await downloadFile(`/delivery-notes/${r.docId}/pdf`, `${r.docNumber}.pdf`); } catch { toast('PDF hiba.', 'error'); }
                        finally { setPdfLoading(false); }
                      }}>PDF letöltés</Button>
                    </>
                  )}
                  {r.category === 'cash-receipt' && (
                    <>
                      <Button onClick={() => navigate('/hazipenztar')}>Megnyitás a házipénztárban</Button>
                      <Button variant="ghost" onClick={async () => {
                        setPdfLoading(true);
                        try { await downloadFile(`/cash-receipts/${r.docId}/pdf`, `${r.docNumber}.pdf`); } catch { toast('PDF hiba.', 'error'); }
                        finally { setPdfLoading(false); }
                      }}>PDF letöltés</Button>
                    </>
                  )}
                </div>
              </div>
            )}

            {isBuyer && (
              <div>
                <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', color: '#d97706', fontWeight: 700, marginBottom: '8px' }}>
                  Bejövő dokumentum műveletek
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {r.category === 'invoice' && (
                    <>
                      <Button onClick={() => navigate(`/bejovo-szamlak`)}>Megnyitás a bejövő számlákban</Button>
                      <Button variant="ghost" onClick={async () => {
                        try {
                          await post('/incoming-invoices', {
                            invoiceNumber: r.docNumber,
                            supplierName: r.issuerName,
                            grossAmount: r.grossAmount ? Math.round(r.grossAmount / 100) : 0,
                            issueDate: r.issueDate,
                            barcodeId: r.barcodeId,
                          });
                          toast('Bejövő számlaként rögzítve.', 'success');
                        } catch (e: any) { toast(e.message || 'Rögzítés sikertelen.', 'error'); }
                      }}>Rögzítés bejövő számlaként</Button>
                    </>
                  )}
                  {r.category === 'delivery-note' && (
                    <div style={{ fontSize: '13px', color: 'var(--slate)', padding: '10px 0' }}>
                      Ez egy bejövő szállítólevél tőled: <b>{r.issuerName}</b>. Az áru átvétele rögzíthető a beszerzés modulban.
                    </div>
                  )}
                </div>
              </div>
            )}

          </div>
        </Card>
      )}

      {/* Tipp — üres állapotban */}
      {!r && !loading && (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--slate)' }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>📷</div>
          <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '6px' }}>Olvasd be a dokumentum vonalkódját</div>
          <div style={{ fontSize: '13px', maxWidth: '400px', margin: '0 auto' }}>
            Nyomd meg a <b>Kamera</b> gombot a beolvasáshoz, vagy írd be kézzel a <b>MOD-</b> kezdetű azonosítót.
            A rendszer felismeri, hogy te vagy-e a kiállító vagy a vevő.
          </div>
        </div>
      )}

      {/* Kamera scanner modal */}
      <BarcodeScanner isOpen={scannerOpen} onClose={() => setScannerOpen(false)} onScan={handleScan} />
    </div>
  );
}
